import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FolderKanban, ArrowRight, Loader2, KeyRound, CheckCircle2 } from "lucide-react";

export default function Login() {
  const { login, verifyOtp, resendOtp } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const resend = async () => {
    try { await resendOtp(mfa.challenge); setCooldown(30); toast.success("A new code has been sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resend the code"); }
  };
  const [fpOpen, setFpOpen] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpBusy, setFpBusy] = useState(false);
  const [fpSent, setFpSent] = useState(false);

  const sendForgot = async () => {
    if (!fpEmail.trim()) return toast.error("Enter your email");
    setFpBusy(true);
    try {
      await fetch(`${API}/auth/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: fpEmail.trim() }),
      });
    } catch (e) { console.debug("forgot request error (kept generic for enumeration safety)", e); }
    setFpSent(true);
    setFpBusy(false);
  };

  const submit = async (em, pw) => {
    setLoading(true);
    try {
      const res = await login(em ?? email, pw ?? password);
      if (res?.mfaRequired) { setMfa({ challenge: res.challenge, email: res.email }); setOtp(""); setCooldown(30); toast.success("We emailed you a sign-in code"); }
      else nav("/");
    } catch (e) {
      if (e?.response?.status === 429) {
        const ra = parseInt(e?.response?.headers?.["retry-after"], 10);
        const secs = Number.isFinite(ra) && ra > 0 ? ra : 60;
        toast.error(`Too many attempts — try again in ${secs} seconds`);
      } else {
        toast.error(e?.response?.data?.detail || "Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    try {
      await verifyOtp(mfa.challenge, otp);
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Incorrect code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-slate-950 p-12 text-white">
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #4F46E5 0, transparent 40%), radial-gradient(circle at 80% 70%, #059669 0, transparent 40%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <img src="/azexpan-logo-white.png" alt="AzexPan" className="h-8" data-testid="login-logo" />
          </div>
        </div>
        <div className="relative space-y-6">
          <h1 className="font-heading text-4xl font-extrabold leading-tight">Structured<br />document collection.</h1>
          <div className="space-y-3 max-w-sm">
            <Feature icon={FolderKanban} color="#818CF8" title="Data Collection" text="Structured document collection from external clients. Full control over every request and everything that comes back: what's outstanding, what's submitted, and what's still missing, all in one place." />
          </div>
        </div>
        <div className="relative text-xs text-white/50 font-mono">&nbsp;</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <img src="/azexpan-logo.png" alt="AzexPan" className="h-8" data-testid="login-logo-mobile" />
          </div>
          {!mfa ? (<>
          <h2 className="font-heading text-2xl font-bold">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Welcome back. Enter your credentials to continue.</p>

          <form className="mt-6 space-y-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="text" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-azx-blue hover:bg-blue-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
            <button type="button" data-testid="forgot-password-link" onClick={() => { setFpSent(false); setFpEmail(email); setFpOpen(true); }} className="mt-3 block w-full text-center text-sm font-medium text-azx-blue hover:underline">
              Forgot password?
            </button>
          </form>
          </>) : (
          <form className="mt-2 space-y-4" onSubmit={(e) => { e.preventDefault(); verify(); }} data-testid="otp-form">
            <h2 className="font-heading text-2xl font-bold">Enter your code</h2>
            <p className="mt-1 text-sm text-slate-500">We emailed a 6-digit code to <span className="font-semibold">{mfa.email}</span>. It expires in 10 minutes.</p>
            <div>
              <Label htmlFor="otp">Verification code</Label>
              <Input id="otp" data-testid="otp-input" inputMode="numeric" maxLength={6} value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="mt-1.5 text-center text-lg tracking-[0.5em]" autoFocus />
            </div>
            <Button type="submit" data-testid="otp-verify" disabled={loading} className="w-full bg-azx-blue hover:bg-blue-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify &amp; sign in <ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
            <button type="button" data-testid="otp-resend" disabled={cooldown > 0} onClick={resend} className="block w-full text-center text-sm font-medium text-azx-blue hover:underline disabled:opacity-50 disabled:no-underline">
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
            <p className="text-center text-xs text-slate-400">Can't access email? Enter a backup recovery code instead.</p>
            <button type="button" data-testid="otp-back" onClick={() => { setMfa(null); setOtp(""); }} className="block w-full text-center text-sm font-medium text-azx-blue hover:underline">
              Back to sign in
            </button>
          </form>
          )}

          <Dialog open={fpOpen} onOpenChange={setFpOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Reset your password</DialogTitle>
                <DialogDescription>Enter your account email and we'll send you a link to set a new password.</DialogDescription>
              </DialogHeader>
              {fpSent ? (
                <div className="py-4 text-center">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">If an account exists for <span className="font-semibold">{fpEmail}</span>, a password reset link has been sent. Please check your inbox.</p>
                  <Button className="mt-5 w-full bg-azx-blue hover:bg-blue-700" data-testid="forgot-close" onClick={() => setFpOpen(false)}>Done</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="fp-email">Email</Label>
                    <Input id="fp-email" data-testid="forgot-email" type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5" onKeyDown={(e) => { if (e.key === "Enter") sendForgot(); }} />
                  </div>
                  <Button data-testid="forgot-submit" disabled={fpBusy} onClick={sendForgot} className="w-full bg-azx-blue hover:bg-blue-700">
                    {fpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="mr-1.5 h-4 w-4" /> Send reset link</>}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, color, title, text }) {
  return (
    <div className="flex gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: color + "22" }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <div className="font-semibold text-white">{title}</div>
        <div className="text-sm text-white/60">{text}</div>
      </div>
    </div>
  );
}
